import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { AI_PROMPT_COMPLEXITY_OPTIONS, AiPromptComplexity, normalizeAiPromptComplexity } from "../../constants/feed-meta";

export interface PromptFormValues {
  content: string;
  complexity: AiPromptComplexity;
}

interface PromptFormDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<PromptFormValues>;
  isLoading?: boolean;
  onClose: () => void;
  onSubmit: (values: PromptFormValues) => Promise<void> | void;
}

// Shared add/edit form for a prompt inside an AI Topic.
export function PromptFormDialog({ open, mode, initial, isLoading, onClose, onSubmit }: PromptFormDialogProps) {
  const [content, setContent] = useState(initial?.content || "");
  const [complexity, setComplexity] = useState<AiPromptComplexity>(normalizeAiPromptComplexity(initial?.complexity));
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setContent(initial?.content || "");
      setComplexity(normalizeAiPromptComplexity(initial?.complexity));
      setError(undefined);
    }
  }, [open, initial?.content, initial?.complexity]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = content.trim();
    if (trimmed.length < 10) {
      setError("Prompt must be at least 10 characters.");
      return;
    }
    setError(undefined);
    await onSubmit({ content: trimmed, complexity });
  };

  return (
    <Dialog open={open} onOpenChange={(value: boolean) => !value && !isLoading && onClose()}>
      <DialogContent className="w-full max-w-[min(100vw-1.5rem,32rem)] max-h-[90vh] overflow-y-auto rounded-2xl p-5 sm:p-6">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add Prompt" : "Edit Prompt"}</DialogTitle>
          <DialogDescription>Add a prompt to this topic, with its complexity level.</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label htmlFor="prompt-content">Prompt *</Label>
            <Textarea
              id="prompt-content"
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (error) setError(undefined);
              }}
              placeholder="Write the AI prompt here..."
              rows={5}
              aria-invalid={!!error}
            />
            {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="prompt-complexity">Complexity Level *</Label>
            <Select value={complexity} onValueChange={(value: string) => setComplexity(normalizeAiPromptComplexity(value))}>
              <SelectTrigger id="prompt-complexity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_PROMPT_COMPLEXITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 pt-4 dark:border-slate-700 sm:flex-row sm:justify-end">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-200 dark:hover:bg-slate-100 text-white dark:text-slate-900 w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  {mode === "create" ? "Add Prompt" : "Save Changes"}
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PromptFormDialog;
